import { Fragment, type ReactNode } from "react";
import {
	Divider,
	Grid2,
	IconButton,
	InputAdornment,
	TextField,
	Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import { DetailSection } from "@/controls/detail-panel/DetailPanel";
import {
	ASSIGNMENT_STATUS_LABEL,
	formatDiameter,
	getAssignmentStatus,
	getRollsetPositions,
	type RollsetPosition,
} from "../models/Rollshop";
import type { RollsetListItem } from "../models/RollshopOperations";
import { EDITABLE_FIELD_SX } from "./compactDialog";
import { ReadOnlyField } from "./ReadOnlyField";

// "Top Roll" / "Bottom Roll" on common stands, "Roll 1" to "Roll 5" on the others.
export const rollLabel = (p: RollsetPosition) =>
	/^\d+$/.test(p.label) ? `Roll ${p.label}` : `${p.label} Roll`;

export const ActionIcon = ({
	title,
	label,
	disabled,
	onClick,
	icon,
}: {
	title: string;
	label: string;
	disabled: boolean;
	onClick: () => void;
	icon: ReactNode;
}) => (
	<Tooltip title={title} placement="left">
		<span>
			<IconButton
				size="small"
				onClick={(e) => {
					// Inside an ActionField the box itself is clickable: one click, one action.
					e.stopPropagation();
					onClick();
				}}
				aria-label={label}
				disabled={disabled}
				sx={{ p: 0.25 }}
			>
				{icon}
			</IconButton>
		</span>
	</Tooltip>
);

// A value changed through a modal, not by typing: editable look while it can be changed, and
// then the whole box opens the change (pointer, like a picker); the read-only look while locked.
export const ActionField = ({
	label,
	value,
	locked,
	actions,
	onClick,
}: {
	label: string;
	value: string | null | undefined;
	locked: boolean;
	actions: ReactNode;
	onClick: () => void;
}) =>
	locked ? (
		<ReadOnlyField label={label} value={value} action={actions} />
	) : (
		<TextField
			label={label}
			value={value || "-"}
			fullWidth
			size="small"
			onClick={onClick}
			sx={{
				...EDITABLE_FIELD_SX,
				"& .MuiInputBase-root, & .MuiInputBase-input": { cursor: "pointer" },
			}}
			slotProps={{
				inputLabel: { shrink: true },
				input: {
					readOnly: true,
					endAdornment: (
						<InputAdornment position="end">{actions}</InputAdornment>
					),
				},
				htmlInput: { tabIndex: -1 },
			}}
		/>
	);

/** Stand, cartridge and both statuses on one line. */
export const RollsetInfoSection = ({
	rollset,
}: {
	rollset: RollsetListItem;
}) => (
	<DetailSection title="Rollset" icon={<InfoOutlinedIcon />}>
		<Grid2 container spacing={1}>
			<Grid2 size={{ xs: 12, md: 3 }}>
				<ReadOnlyField label="Stand" value={rollset.StandName} />
			</Grid2>
			<Grid2 size={{ xs: 12, md: 3 }}>
				<ReadOnlyField label="Cartridge" value={rollset.CartridgeName} />
			</Grid2>
			<Grid2 size={{ xs: 12, md: 3 }}>
				<ReadOnlyField
					label="Status"
					value={ASSIGNMENT_STATUS_LABEL[getAssignmentStatus(rollset)]}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 3 }}>
				<ReadOnlyField label="Op. Status" value={rollset.StatusName} />
			</Grid2>
		</Grid2>
	</DetailSection>
);

interface RollsSectionProps {
	rollset: RollsetListItem;
	/** Common stands also list Roll 3/4/5 when they hold a roll (legacy Rollsets screen). */
	includeExtraCommon?: boolean;
	/** Without it the rolls are read-only. */
	onChangeRoll?: (position: RollsetPosition) => void;
	locked?: boolean;
	lockedReason?: string;
}

/** One line per roll (name and diameter), a divider between rolls. Empty positions read "None". */
export const RollsSection = ({
	rollset,
	includeExtraCommon = false,
	onChangeRoll,
	locked = false,
	lockedReason = "",
}: RollsSectionProps) => {
	const isCommon = !rollset.StandType || rollset.StandType === "COMMON";
	const extraCommon: RollsetPosition[] =
		isCommon && includeExtraCommon
			? (
					[
						[rollset.Roll3Id, rollset.Roll3Name, rollset.Roll3Diameter],
						[rollset.Roll4Id, rollset.Roll4Name, rollset.Roll4Diameter],
						[rollset.Roll5Id, rollset.Roll5Name, rollset.Roll5Diameter],
					] as const
				)
					.map(([rollId, rollName, diameter], index) => ({
						position: String(index + 3),
						label: String(index + 3),
						rollId,
						rollName,
						diameter,
					}))
					.filter((p) => p.rollId != null || p.rollName)
			: [];
	const positions = [...getRollsetPositions(rollset), ...extraCommon];

	return (
		<DetailSection title="Rolls" icon={<TrackChangesIcon />}>
			<Grid2 container spacing={1}>
				{positions.map((p, index) => (
					<Fragment key={p.position}>
						{index > 0 && (
							<Grid2 size={12}>
								<Divider />
							</Grid2>
						)}
						<Grid2 size={{ xs: 12, md: 6 }}>
							{onChangeRoll ? (
								<ActionField
									label={rollLabel(p)}
									value={p.rollName ?? "None"}
									locked={locked}
									onClick={() => onChangeRoll(p)}
									actions={
										<ActionIcon
											title={locked ? lockedReason : `Change ${rollLabel(p)}`}
											label={`Change ${rollLabel(p)}`}
											disabled={locked}
											icon={<EditOutlinedIcon fontSize="small" />}
											onClick={() => onChangeRoll(p)}
										/>
									}
								/>
							) : (
								<ReadOnlyField
									label={rollLabel(p)}
									value={p.rollName ?? "None"}
								/>
							)}
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<ReadOnlyField
								label={`${rollLabel(p)} Diameter`}
								value={p.rollId == null ? null : formatDiameter(p.diameter)}
								numeric
							/>
						</Grid2>
					</Fragment>
				))}
			</Grid2>
		</DetailSection>
	);
};
